/* ============================================================
   Kanda mark + wordmark lockup.

   Built against the supplied brand guidelines rather than traced from a
   thumbnail. The guidelines describe it as an "abstract infinity-loop /
   belt shape icon with topographic texture", and the mono variants make
   the topology unambiguous: it is ONE closed ring — a bone or bowtie
   outline with a pinched waist — not two separate ribbons. An earlier
   pass drew two overlapping strands, which reproduced the seams but got
   the silhouette wrong.

   The two-colour treatment splits that single ring horizontally:
   Midnight Indigo above, Safiri Amber below, with a one-unit overlap at
   the seam so no hairline shows between them.

   TEXTURE. Both halves carry fine topographic contour lines. Applied
   only above a size threshold — at favicon scale they turn to mud and
   only the silhouette survives, which is what the 32px icon in the
   guidelines relies on.

   COLOURS come from the four-colour system and must not be altered:
   #1B1F3B Midnight Indigo, #C75C2E Safiri Amber, #F5F0E8 Warm Sand,
   #4ECDC4 Fresh Mint. "Do not alter brand colours" is rule one of the
   do-not-use page.
   ============================================================ */

import { html, raw } from '../shared/html.js';

/* Outer and inner boundary of the belt, on a 256 × 176 grid.
   Filled even-odd so the pair reads as a ring. */
export const RING_PATH = [
  // outer
  'M60 14',
  'C97 14 110 53 128 53',
  'C146 53 159 14 196 14',
  'C227 14 250 31 250 88',
  'C250 145 227 162 196 162',
  'C159 162 146 123 128 123',
  'C110 123 97 162 60 162',
  'C29 162 6 145 6 88',
  'C6 31 29 14 60 14',
  'Z',
  // inner
  'M66 50',
  'C89 50 105 77 128 77',
  'C151 77 167 50 190 50',
  'C209 50 220 61 220 88',
  'C220 115 209 126 190 126',
  'C167 126 151 99 128 99',
  'C105 99 89 126 66 126',
  'C47 126 36 115 36 88',
  'C36 61 47 50 66 50',
  'Z'
].join(' ');

const TEXTURE_MIN_HEIGHT = 40;
const SEAM = 88; // the horizontal split, at the waist

let seq = 0;

/** Topographic contour lines — the mark's signature texture. */
export function contourPattern(id, opacity = 0.26) {
  return `
    <pattern id="${id}" width="34" height="22" patternUnits="userSpaceOnUse"
             patternTransform="rotate(-14)">
      <path d="M-6 4 Q 9 10 24 4 T 54 4" fill="none" stroke="#fff"
            stroke-opacity="${opacity}" stroke-width="1.15"/>
      <path d="M-6 11 Q 12 17 27 11 T 57 11" fill="none" stroke="#fff"
            stroke-opacity="${opacity * 0.7}" stroke-width="1"/>
      <path d="M-6 18 Q 8 23 23 18 T 53 18" fill="none" stroke="#fff"
            stroke-opacity="${opacity * 0.9}" stroke-width="1.15"/>
    </pattern>`;
}

/**
 * @param {object} opts
 *   height    rendered height in px
 *   top       colour of the upper half
 *   bottom    colour of the lower half
 *   mono      single colour for the whole ring
 *   textured  force the topographic texture on or off
 */
export function mark({
  height = 24,
  top = 'var(--logo-strand-a)',
  bottom = 'var(--logo-strand-b)',
  mono = null,
  textured = null
} = {}) {
  const width = Math.round((height * 256) / 176);
  const upper = mono || top;
  const lower = mono || bottom;
  const showTexture = textured === null ? height >= TEXTURE_MIN_HEIGHT : textured;
  const id = `k${(seq += 1)}`;

  return `
    <svg width="${width}" height="${height}" viewBox="0 0 256 176"
         xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <defs>
        ${showTexture ? contourPattern(`${id}-grain`) : ''}
        <clipPath id="${id}-top"><rect x="0" y="0" width="256" height="${SEAM + 1}"/></clipPath>
        <clipPath id="${id}-bot"><rect x="0" y="${SEAM}" width="256" height="${176 - SEAM}"/></clipPath>
      </defs>
      <g fill-rule="evenodd">
        <path d="${RING_PATH}" fill="${lower}" clip-path="url(#${id}-bot)"/>
        <path d="${RING_PATH}" fill="${upper}" clip-path="url(#${id}-top)"/>
        ${showTexture ? `<path d="${RING_PATH}" fill="url(#${id}-grain)"/>` : ''}
      </g>
    </svg>`;
}

/**
 * Full lockup: mark, wordmark and descriptor — the horizontal variant.
 * Clear space equal to the wordmark cap height is held by .logo-lockup.
 */
export function Logo({
  height = 22,
  showDescriptor = true,
  name = 'Kanda',
  descriptor = 'Logistics Advisory'
} = {}) {
  const node = document.createElement('span');
  node.className = 'logo-lockup';

  node.innerHTML = html`
    <span class="logo-mark">${raw(mark({ height: Math.round(height * 1.32) }))}</span>
    <span class="logo-text">
      <span class="logo-name" style="font-size: ${height}px;">${name}</span>
      ${showDescriptor ? html`<span class="logo-descriptor">${descriptor}</span>` : ''}
    </span>
  `;

  return node;
}
